import React from 'react';
import { styled } from '@mui/material/styles';
import Link from 'next/link';
import Box from '@mui/material/Box';
import { KitButton } from 'components/common/cms-kit';
import { useTheme } from '@mui/material/styles';
import { getTextAlignmentClass } from '../../services/generalUtils';
import { getContentStyle } from '../../services/styleUtils/contentStyleUtils';
import SubTitle from '../common/SubTitle';

const GrayBackground = styled('div')(({ $imageAlign, $imageExists }) => ({
  '@media screen and (min-width: 992px)': {
    flexDirection: $imageAlign === 'left'
      ? $imageExists
        ? 'row-reverse'
        : 'row'
      : 'row',
    padding: '5px 0 45px 0',
  },
  '@media screen and (max-width: 991px)': {
    padding: 0,
  },
}));

const Frame = styled('div')(({ $imageAlign, $imageExists }) => ({
  display: 'flex',
  '@media screen and (min-width: 992px)': {
    flexDirection: $imageAlign === 'right'
      ? $imageExists
        ? 'row-reverse'
        : 'row'
      : 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  '@media screen and (max-width: 991px)': {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    margin: '0 0 20px 0',
    padding: 0,
  },
}));

const ContentContainer = styled('div')({
  display: 'flex',
  '@media screen and (min-width: 992px)': {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    verticalAlign: 'top',
    marginTop: '60px',
    width: '50%',
  },
  '@media screen and (max-width: 991px)': {
    textAlign: 'left',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

const ImageContainer = styled('div')(({ $imageExists }) => ({
  '@media screen and (min-width: 992px)': {
    justifyContent: 'left',
    margin: 0,
    ...($imageExists ? { maxWidth: '60%' } : { display: 'none' }),
    ...($imageExists ? { maxHeight: '95%' } : { display: 'none' }),
  },
  '@media screen and (max-width: 991px)': {
    width: '100%',
    padding: '5px',
    ...($imageExists ? { width: '100%' } : { display: 'none' }),
    ...($imageExists ? { height: 'auto' } : { display: 'none' }),
  },
}));

const Title = styled('div')(({ $titleColor }) => ({
  textTransform: 'uppercase',
  fontWeight: 600,
  '@media screen and (min-width: 992px)': {
    margin: '15px 5px 0 5px',
    minHeight: '25px',
    color: $titleColor,
    fontSize: '2.375rem',
    lineHeight: '2.688rem',
  },
  '@media screen and (max-width: 991px)': {
    margin: '10px',
    color: $titleColor,
    fontSize: '1.625rem',
    lineHeight: '1.875rem',
    padding: '0 5px',
  },
}));

const ButtonContainer = styled('div')({
  '@media screen and (min-width: 992px)': {
    margin: '10px 5px 5px 5px',
  },
  '@media screen and (max-width: 991px)': {
    padding: '0 5px',
  },
});

const SummaryWithImage = ({ module }) => {
  const { fields } = module;
  const theme = useTheme();

  const imageExists =
    fields.moduleImage &&
    fields.moduleImage.url &&
    fields.moduleImage.url.length > 0;

  const subTitleStyle = fields?.subTitle
    ? getContentStyle(theme, fields?.subTitleStyle?.fields ?? {})
    : {};

  return (
    <React.Fragment>
      <GrayBackground
        $imageAlign={fields.imageAlign}
        $imageExists={imageExists}
        style={{
          backgroundColor: `rgba(217, 217, 217, 0.3)`,
        }}
      >
        <Box
          sx={{
            width: '100%',
            ml: 'auto',
            mr: 'auto',
            maxWidth: { sm: '540px', md: '768px', lg: '1140px', xl: '1180px' },
          }}
        >
          <Frame $imageAlign={fields.imageAlign} $imageExists={imageExists}>
            <ImageContainer $imageExists={imageExists}>
              <img
                src={fields?.moduleImage?.url}
                alt={fields?.moduleImage?.label}
                width={'100%'}
                height={'auto'}
              />
            </ImageContainer>
            <ContentContainer>
              <Title $titleColor={fields.titleColor}>{fields.title}</Title>
              {fields?.subTitle && (
                <SubTitle
                  text={fields.subTitle}
                  styleProps={subTitleStyle}
                  className={`w-full ${getTextAlignmentClass(subTitleStyle?.alignment, 'left')} m-[10px] lg:mt-[15px] lg:mr-[5px] lg:mb-0 lg:ml-[5px] lg:p-0 md:px-[18px] px-[18px]`}
                />
              )}
              {fields.actionButtonUrl &&
                fields.actionButtonUrl.href &&
                fields.actionButtonUrl.href.length > 0 && (
                  <ButtonContainer>
                    <Link
                      href={fields.actionButtonUrl.href}
                      target={fields.actionButtonUrl.target}
                      rel={`noopener noreferrer${fields.addNoFollowtoActionUrl === 'true' ? ' nofollow' : ''}`}>
                      <KitButton
                        round
                        size="lg"
                        style={{
                          backgroundColor: theme.palette.primary.main,
                        }}
                      >
                        {fields.actionButtonUrl.text}
                      </KitButton>
                    </Link>
                  </ButtonContainer>
                )}
            </ContentContainer>
          </Frame>
        </Box>
      </GrayBackground>
    </React.Fragment>
  );
};

export default SummaryWithImage;
